/**
 * Entity-page Ask Insights *entry* — pins the current object and opens `/ai`.
 * Not a panel. Subject Workspace uses {@link SubjectAskPanel} on md+.
 */
import { useFeatureFlags } from "../../api/feature-flags.js";

import type { AiSourceRef } from "@l30/shared";

export type AskInsightsSource = AiSourceRef & { title?: string };

export function askInsightsUrl(source: AskInsightsSource): string {
  const params = new URLSearchParams({
    sourceType: source.type,
    sourceId: source.id,
    ...(source.title ? { sourceTitle: source.title } : {}),
  });
  return `/ai?${params.toString()}`;
}

interface AskInsightsEntryProps {
  source: AskInsightsSource;
  /** `fab` matches the previous Subject Workspace chip; default keeps module-page chip. */
  variant?: "chip" | "fab";
}

/** Floating entry on entity pages — deep-links to `/ai` with a pinned source. */
export function AskInsightsEntry({ source, variant = "chip" }: AskInsightsEntryProps) {
  const flags = useFeatureFlags();
  const on = flags.data?.flags.find((f) => f.key === "insights_ai")?.enabled === true;
  if (!on) return null;

  const fab = variant === "fab";
  return (
    <div className="fixed bottom-4 right-4 z-40 md:bottom-10 md:right-10">
      {/* Plain anchor: query pins are not part of the typed route tree. */}
      <a
        href={askInsightsUrl(source)}
        title={source.title ? `Ask about ${source.title}` : "Ask Insights"}
        className={
          fab
            ? "inline-flex size-14 items-center justify-center rounded-full bg-(--color-rule-accent) text-sm font-semibold text-fg shadow-modal"
            : "inline-flex items-center justify-center rounded-soft-md border border-(--color-rule-accent) px-3 py-1.5 text-xs font-medium text-fg hover:bg-surface-muted"
        }
      >
        {fab ? "Ask" : "Ask Insights"}
      </a>
    </div>
  );
}

/** Inline link for list rows / cards (same deep-link contract as the FAB). */
export function AskInsightsLink({
  source,
  className,
}: {
  source: AskInsightsSource;
  className?: string;
}) {
  const flags = useFeatureFlags();
  const on = flags.data?.flags.find((f) => f.key === "insights_ai")?.enabled === true;
  if (!on) return null;

  return (
    <a
      href={askInsightsUrl(source)}
      className={
        className ??
        "inline-flex items-center justify-center rounded-soft-md px-2 py-1 text-xs font-medium text-brand hover:bg-surface-muted"
      }
    >
      Ask Insights
    </a>
  );
}
